import formatMediaObject from '../utils/formatters/format-media-object';
import { formatDietaryLabels } from './dietary-labels';

/**
 * Format the breadcrumbs for the product page.
 *
 * @param {object} context
 * @param {object} attributes
 *
 * @returns {({text: string, to: string}|{active: boolean, text: string})[]}
 */
export const formatBreadcrumbsForProductPage = (context, attributes) => {
    const product = attributes.product
        ?.data
        ?.attributes;
    const category = product
        ?.category
        ?.data
        ?.attributes;

    return [
        {
            text: context.$snippet('home'),
            to: '/',
        },
        {
            text: context.$snippet('menu'),
            to: context.$snippet('menu_page_link'),
        },
        {
            text: category?.name,
            to: category?.page?.data?.attributes?.uri,
        },
        {
            text: product?.marketing_title,
            active: true,
        },
    ].filter(({ to, active }) => to || active);
};

/**
 * Format the nutrition facts for the product page.
 *
 * @param {object} product
 * @param {object} options
 * @param {boolean} options.hasPdfLink
 *
 * @returns {{description: string, title: string}[]}
 */
export const formatNutritionFacts = (product, { hasPdfLink = false } = {}) => {
    // The keys are the keys of the product object and the values are the titles of the nutrition facts.
    const nutritionFactKeys = {
        calories: 'energy (kcal)',
        fat: 'fat (g)',
        fat_saturated: 'of which saturates (g)',
        protein: 'protein (g)',
        carbs: 'carbohydrates (g)',
        sugars: 'of which sugars (g)',
        fibre: 'fibre (g)',
        salt: `salt (g)${ hasPdfLink ? '*' : '' }`,
        plant_points: 'plant points',
    };

    // Only return the nutrition facts that are present in the product object.
    return Object.keys(nutritionFactKeys).flatMap((key) => {
        if (product[key]) {
            return {
                title: nutritionFactKeys[key],
                description: product[key],
            };
        }

        return [];
    });
};

/**
 * Format the allergens for the product page.
 *
 * @param {Array} allergens
 *
 * @returns {string}
 */
export const formatAllergens = (allergens = []) => {
    return allergens.map(({ attributes }) => attributes.name)
        .join(', ')
        .toLowerCase();
};

/**
 * Format the data to be passed to the ProductPage component.
 *
 * @param {object} context
 * @param {object} attributes
 *
 * @returns {object}
 */
export const formatDataForProductPage = (context, attributes) => {
    const product = attributes.product
        ?.data
        ?.attributes;
    const category = product
        ?.category
        ?.data
        ?.attributes;

    return {
        category: {
            text: category?.name,
            to: category?.page?.data?.attributes?.uri,
        },
        name: product?.marketing_title,
        description: product?.marketing_product_description,
        calories: `${ product?.calories } kcal`,
        image: formatMediaObject.call(context, product?.website_product_image),
        imageSm: formatMediaObject.call(context, product?.website_product_image),
        dietaryLabels: formatDietaryLabels(context, product?.dietary_labels?.data),
        contains: formatAllergens(product?.allergens?.data),
        allergenWarning: context.$snippet('menu_allergens_text'),
        mayContain: formatAllergens(product?.may_contain?.data),
        nutritionFacts: formatNutritionFacts(product, {
            hasPdfLink: Boolean(attributes.pdf_button_text && attributes.pdf_button_file),
        }),
        ingredients: product?.ingredients,
        storyTitle: attributes.productPageTemplate_story_title,
        storyContent: attributes.productPageTemplate_story_content,
        heroButtonLink: context.$snippet('find_an_itsu_link'),
        heroButtonText: context.$snippet('find_an_itsu', 'find an itsu'),
        readMore: {
            buttonText: attributes.read_more?.button_text,
            content: attributes.read_more?.content,
        },
        pdfButtonText: attributes.pdf_button_text,
        pdfButtonLink: formatMediaObject.call(context, attributes.pdf_button_file).src,
        showSoySauceModalButton: attributes.show_soy_sauce_modal_button,
        plantPoints: product?.plant_points ? {
            points: product?.plant_points,
            description: product?.plant_points_description,
            image: formatMediaObject.call(context, product?.plant_points_image),
            background: product?.plant_points_background,
        } : {},
    };
};
